export interface ThemeColors {
  // Primary colors
  primary: string;
  secondary: string;
  tertiary: string;
  
  // Background colors
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  surface: string;
  surfaceHover: string;
  
  // Text colors
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textInverse: string;
  
  // Border colors
  border: string;
  borderHover: string;
  borderActive: string;
  
  // Status colors
  success: string;
  warning: string;
  error: string;
  info: string;
  
  // Trading colors
  buy: string;
  sell: string;
  neutral: string;
  
  // Chart colors
  chartBackground: string;
  chartGrid: string;
  chartText: string;
  candleUp: string;
  candleDown: string;
  wickUp: string;
  wickDown: string;
  
  // Volume heatmap colors
  heatmapLow: string;
  heatmapMedium: string;
  heatmapHigh: string;
  
  // Neon accent colors
  neonCyan: string;
  neonPurple: string;
  neonPink: string;
  neonGreen: string;
  neonOrange: string;
  
  // Rarity colors
  rarityCommon: string;
  rarityUncommon: string;
  rarityRare: string;
  rarityEpic: string;
  rarityLegendary: string;
  rarityMythic: string;
}

export interface Theme {
  name: string;
  displayName: string;
  description: string;
  colors: ThemeColors;
  isDark: boolean;
}

const JESTER_DECK_COLORS: ThemeColors = {
  primary: '#ff8a13',
  secondary: '#ff684f',
  tertiary: '#22d6d0',
  background: '#02070a',
  backgroundSecondary: '#041014',
  backgroundTertiary: '#061217',
  surface: '#061217',
  surfaceHover: '#0a1920',
  textPrimary: '#f4d7aa',
  textSecondary: '#b9a183',
  textTertiary: '#7f6e5d',
  textInverse: '#02070a',
  border: 'rgba(139, 72, 22, 0.52)',
  borderHover: 'rgba(255, 138, 19, 0.46)',
  borderActive: '#ff8a13',
  success: '#18d47f',
  warning: '#ff8a13',
  error: '#ff5545',
  info: '#22d6d0',
  buy: '#18d47f',
  sell: '#ff5545',
  neutral: '#7f6e5d',
  chartBackground: '#061217',
  chartGrid: 'rgba(34, 214, 208, 0.14)',
  chartText: '#f4d7aa',
  candleUp: '#18d47f',
  candleDown: '#ff5545',
  wickUp: '#18d47f',
  wickDown: '#ff5545',
  heatmapLow: 'rgba(34, 214, 208, 0.25)',
  heatmapMedium: 'rgba(24, 212, 127, 0.35)',
  heatmapHigh: 'rgba(255, 138, 19, 0.45)',
  neonCyan: '#22d6d0',
  neonPurple: '#ff8a13',
  neonPink: '#ff5545',
  neonGreen: '#18d47f',
  neonOrange: '#f7bd76',
  rarityCommon: '#b9a183',
  rarityUncommon: '#18d47f',
  rarityRare: '#22d6d0',
  rarityEpic: '#ff8a13',
  rarityLegendary: '#f7bd76',
  rarityMythic: '#ff684f',
};

export const themes: Record<string, Theme> = {
  'jester-deck': {
    name: 'jester-deck',
    displayName: 'Jester Deck',
    description: 'Command deck: teal void, copper frames, amber controls, cyan data',
    isDark: true,
    colors: JESTER_DECK_COLORS,
  },
  // Default Dark Theme - Quant desk aesthetic (royal purple + antique gold)
  'dark-cyber': {
    name: 'dark-cyber',
    displayName: 'Cyber Dark',
    description: 'Quant desk theme: deep royal purple, antique gold accents',
    isDark: true,
    colors: {
      primary: '#00e6ff',
      secondary: '#ff007d',
      tertiary: '#4B0082',
      
      background: '#0d0d0d',
      backgroundSecondary: '#151515',
      backgroundTertiary: '#1A1A1C',
      surface: '#3a3a4e',
      surfaceHover: '#4a4a5e',
      
      textPrimary: '#f2f2eb',
      textSecondary: '#e6e6e6',
      textTertiary: '#9aa',
      textInverse: '#000000',
      
      border: '#3a3a4e',
      borderHover: '#4a4a5e',
      borderActive: '#00e6ff',
      
      success: '#00ff88',
      warning: '#fff933',
      error: '#ff007d',
      info: '#00e6ff',
      
      buy: '#00ff88',
      sell: '#ff007d',
      neutral: '#9aa',
      
      chartBackground: '#050208',
      chartGrid: 'rgba(109, 40, 217, 0.22)',
      chartText: '#f5f0ff',
      candleUp: '#39ff14',
      candleDown: '#ff3d5a',
      wickUp: '#39ff14',
      wickDown: '#ff3d5a',
      
      heatmapLow: 'rgba(0, 230, 255, 0.3)',
      heatmapMedium: 'rgba(0, 255, 136, 0.5)',
      heatmapHigh: 'rgba(255, 0, 125, 0.7)',
      
      neonCyan: '#00e6ff',
      neonPurple: '#4B0082',
      neonPink: '#ff007d',
      neonGreen: '#00ff88',
      neonOrange: '#fff933',
      
      rarityCommon: '#9aa',
      rarityUncommon: '#00ff88',
      rarityRare: '#00e6ff',
      rarityEpic: '#4B0082',
      rarityLegendary: '#fff933',
      rarityMythic: '#ff007d',
    }
  },

  // Light Theme
  'light-modern': {
    name: 'light-modern',
    displayName: 'Modern Light',
    description: 'Clean light theme with modern colors',
    isDark: false,
    colors: {
      primary: '#1976d2',
      secondary: '#d32f2f',
      tertiary: '#388e3c',
      
      background: '#fafafa',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#f5f5f5',
      surface: '#e0e0e0',
      surfaceHover: '#d0d0d0',
      
      textPrimary: '#000000',
      textSecondary: '#333333',
      textTertiary: '#666666',
      textInverse: '#ffffff',
      
      border: '#e0e0e0',
      borderHover: '#d0d0d0',
      borderActive: '#1976d2',
      
      success: '#4caf50',
      warning: '#ff9800',
      error: '#f44336',
      info: '#2196f3',
      
      buy: '#4caf50',
      sell: '#f44336',
      neutral: '#666666',
      
      chartBackground: '#ffffff',
      chartGrid: '#e0e0e0',
      chartText: '#000000',
      candleUp: '#4caf50',
      candleDown: '#f44336',
      wickUp: '#4caf50',
      wickDown: '#f44336',
      
      heatmapLow: 'rgba(33, 150, 243, 0.3)',
      heatmapMedium: 'rgba(76, 175, 80, 0.5)',
      heatmapHigh: 'rgba(255, 152, 0, 0.7)',
      
      neonCyan: '#00bcd4',
      neonPurple: '#9c27b0',
      neonPink: '#e91e63',
      neonGreen: '#4caf50',
      neonOrange: '#ff9800',
      
      rarityCommon: '#9e9e9e',
      rarityUncommon: '#4caf50',
      rarityRare: '#2196f3',
      rarityEpic: '#9c27b0',
      rarityLegendary: '#ff9800',
      rarityMythic: '#e91e63',
    }
  },

  // Matrix Theme
  'matrix-green': {
    name: 'matrix-green',
    displayName: 'Matrix Green',
    description: 'Classic matrix green on black theme',
    isDark: true,
    colors: {
      primary: '#00ff00',
      secondary: '#ff0000',
      tertiary: '#ffff00',
      
      background: '#000000',
      backgroundSecondary: '#001100',
      backgroundTertiary: '#002200',
      surface: '#003300',
      surfaceHover: '#004400',
      
      textPrimary: '#00ff00',
      textSecondary: '#00cc00',
      textTertiary: '#009900',
      textInverse: '#000000',
      
      border: '#003300',
      borderHover: '#004400',
      borderActive: '#00ff00',
      
      success: '#00ff00',
      warning: '#ffff00',
      error: '#ff0000',
      info: '#00ffff',
      
      buy: '#00ff00',
      sell: '#ff0000',
      neutral: '#666666',
      
      chartBackground: '#000000',
      chartGrid: '#003300',
      chartText: '#00ff00',
      candleUp: '#00ff00',
      candleDown: '#ff0000',
      wickUp: '#00ff00',
      wickDown: '#ff0000',
      
      heatmapLow: 'rgba(0, 255, 0, 0.2)',
      heatmapMedium: 'rgba(0, 255, 0, 0.4)',
      heatmapHigh: 'rgba(0, 255, 0, 0.6)',
      
      neonCyan: '#00ffff',
      neonPurple: '#ff00ff',
      neonPink: '#ff0080',
      neonGreen: '#00ff00',
      neonOrange: '#ff8000',
      
      rarityCommon: '#666666',
      rarityUncommon: '#00ff00',
      rarityRare: '#00ffff',
      rarityEpic: '#ff00ff',
      rarityLegendary: '#ffff00',
      rarityMythic: '#ff0080',
    }
  },

  // Ocean Theme
  'ocean-blue': {
    name: 'ocean-blue',
    displayName: 'Ocean Blue',
    description: 'Deep ocean blue theme with wave-like gradients',
    isDark: true,
    colors: {
      primary: '#00bfff',
      secondary: '#ff6b6b',
      tertiary: '#4ecdc4',
      
      background: '#001122',
      backgroundSecondary: '#002244',
      backgroundTertiary: '#003366',
      surface: '#004488',
      surfaceHover: '#0055aa',
      
      textPrimary: '#ffffff',
      textSecondary: '#b3d9ff',
      textTertiary: '#6699cc',
      textInverse: '#000000',
      
      border: '#003366',
      borderHover: '#004488',
      borderActive: '#00bfff',
      
      success: '#00ff88',
      warning: '#ffaa00',
      error: '#ff4444',
      info: '#00bfff',
      
      buy: '#00ff88',
      sell: '#ff4444',
      neutral: '#6699cc',
      
      chartBackground: '#001122',
      chartGrid: '#003366',
      chartText: '#ffffff',
      candleUp: '#00ff88',
      candleDown: '#ff4444',
      wickUp: '#00ff88',
      wickDown: '#ff4444',
      
      heatmapLow: 'rgba(0, 191, 255, 0.3)',
      heatmapMedium: 'rgba(0, 255, 136, 0.5)',
      heatmapHigh: 'rgba(255, 170, 0, 0.7)',
      
      neonCyan: '#00bfff',
      neonPurple: '#8a2be2',
      neonPink: '#ff69b4',
      neonGreen: '#00ff88',
      neonOrange: '#ffaa00',
      
      rarityCommon: '#6699cc',
      rarityUncommon: '#00ff88',
      rarityRare: '#00bfff',
      rarityEpic: '#8a2be2',
      rarityLegendary: '#ffaa00',
      rarityMythic: '#ff69b4',
    }
  },

  // Sunset Theme
  'sunset-orange': {
    name: 'sunset-orange',
    displayName: 'Sunset Orange',
    description: 'Warm sunset theme with orange and red tones',
    isDark: true,
    colors: {
      primary: '#ff6b35',
      secondary: '#f7931e',
      tertiary: '#ffd23f',
      
      background: '#2c1810',
      backgroundSecondary: '#4a2c1a',
      backgroundTertiary: '#6b3f2a',
      surface: '#8b5a3a',
      surfaceHover: '#a66b4a',
      
      textPrimary: '#ffffff',
      textSecondary: '#ffd3b6',
      textTertiary: '#cc9a7a',
      textInverse: '#000000',
      
      border: '#6b3f2a',
      borderHover: '#8b5a3a',
      borderActive: '#ff6b35',
      
      success: '#4caf50',
      warning: '#ff9800',
      error: '#f44336',
      info: '#ff6b35',
      
      buy: '#4caf50',
      sell: '#f44336',
      neutral: '#cc9a7a',
      
      chartBackground: '#2c1810',
      chartGrid: '#6b3f2a',
      chartText: '#ffffff',
      candleUp: '#4caf50',
      candleDown: '#f44336',
      wickUp: '#4caf50',
      wickDown: '#f44336',
      
      heatmapLow: 'rgba(255, 107, 53, 0.3)',
      heatmapMedium: 'rgba(255, 193, 7, 0.5)',
      heatmapHigh: 'rgba(255, 87, 34, 0.7)',
      
      neonCyan: '#00bcd4',
      neonPurple: '#9c27b0',
      neonPink: '#e91e63',
      neonGreen: '#4caf50',
      neonOrange: '#ff6b35',
      
      rarityCommon: '#cc9a7a',
      rarityUncommon: '#4caf50',
      rarityRare: '#00bcd4',
      rarityEpic: '#9c27b0',
      rarityLegendary: '#ff9800',
      rarityMythic: '#e91e63',
    }
  },

  // Purple Haze Theme
  'purple-haze': {
    name: 'purple-haze',
    displayName: 'Purple Haze',
    description: 'Mystical purple theme with cosmic vibes',
    isDark: true,
    colors: {
      primary: '#6366f1',
      secondary: '#ec4899',
      tertiary: '#10b981',
      
      background: '#0a0a0f',
      backgroundSecondary: '#1a1a2e',
      backgroundTertiary: '#2a2a3e',
      surface: '#3a3a4e',
      surfaceHover: '#4a4a5e',
      
      textPrimary: '#ffffff',
      textSecondary: '#e0e7ff',
      textTertiary: '#a5b4fc',
      textInverse: '#000000',
      
      border: '#2a2a3e',
      borderHover: '#3a3a4e',
      borderActive: '#6366f1',
      
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#6366f1',
      
      buy: '#10b981',
      sell: '#ef4444',
      neutral: '#a5b4fc',
      
      chartBackground: '#0a0a0f',
      chartGrid: '#2a2a3e',
      chartText: '#ffffff',
      candleUp: '#10b981',
      candleDown: '#ef4444',
      wickUp: '#10b981',
      wickDown: '#ef4444',
      
      heatmapLow: 'rgba(99, 102, 241, 0.3)',
      heatmapMedium: 'rgba(16, 185, 129, 0.5)',
      heatmapHigh: 'rgba(236, 72, 153, 0.7)',
      
      neonCyan: '#06b6d4',
      neonPurple: '#6366f1',
      neonPink: '#ec4899',
      neonGreen: '#10b981',
      neonOrange: '#f59e0b',
      
      rarityCommon: '#a5b4fc',
      rarityUncommon: '#10b981',
      rarityRare: '#06b6d4',
      rarityEpic: '#6366f1',
      rarityLegendary: '#f59e0b',
      rarityMythic: '#ec4899',
    }
  },

  // High-contrast: White on Black
  'mono-dark': {
    name: 'mono-dark',
    displayName: 'Mono Dark (White on Black)',
    description: 'High-contrast theme with white on deep black',
    isDark: true,
    colors: {
      primary: '#ffffff',
      secondary: '#cccccc',
      tertiary: '#999999',

      background: '#000000',
      backgroundSecondary: '#0d0d0d',
      backgroundTertiary: '#1a1a1a',
      surface: '#222222',
      surfaceHover: '#2a2a2a',

      textPrimary: '#ffffff',
      textSecondary: '#e6e6e6',
      textTertiary: '#b3b3b3',
      textInverse: '#000000',

      border: '#333333',
      borderHover: '#444444',
      borderActive: '#ffffff',

      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#60a5fa',

      buy: '#22c55e',
      sell: '#ef4444',
      neutral: '#b3b3b3',

      chartBackground: '#000000',
      chartGrid: '#333333',
      chartText: '#ffffff',
      candleUp: '#22c55e',
      candleDown: '#ef4444',
      wickUp: '#22c55e',
      wickDown: '#ef4444',

      heatmapLow: 'rgba(255,255,255,0.18)',
      heatmapMedium: 'rgba(255,255,255,0.35)',
      heatmapHigh: 'rgba(255,255,255,0.55)',

      neonCyan: '#e5e7eb',
      neonPurple: '#d1d5db',
      neonPink: '#f3f4f6',
      neonGreen: '#22c55e',
      neonOrange: '#f59e0b',

      rarityCommon: '#b3b3b3',
      rarityUncommon: '#22c55e',
      rarityRare: '#60a5fa',
      rarityEpic: '#a78bfa',
      rarityLegendary: '#f59e0b',
      rarityMythic: '#ef4444',
    }
  },

  // High-contrast: Black on White
  'mono-light': {
    name: 'mono-light',
    displayName: 'Mono Light (Black on White)',
    description: 'High-contrast theme with black on pure white',
    isDark: false,
    colors: {
      primary: '#000000',
      secondary: '#333333',
      tertiary: '#666666',

      background: '#ffffff',
      backgroundSecondary: '#f8f9fa',
      backgroundTertiary: '#f1f3f5',
      surface: '#e9ecef',
      surfaceHover: '#dee2e6',

      textPrimary: '#000000',
      textSecondary: '#222222',
      textTertiary: '#555555',
      textInverse: '#ffffff',

      border: '#cfd3d8',
      borderHover: '#bfc5cb',
      borderActive: '#000000',

      success: '#16a34a',
      warning: '#d97706',
      error: '#dc2626',
      info: '#2563eb',

      buy: '#16a34a',
      sell: '#dc2626',
      neutral: '#666666',

      chartBackground: '#ffffff',
      chartGrid: '#e5e7eb',
      chartText: '#000000',
      candleUp: '#16a34a',
      candleDown: '#dc2626',
      wickUp: '#16a34a',
      wickDown: '#dc2626',

      heatmapLow: 'rgba(0,0,0,0.10)',
      heatmapMedium: 'rgba(0,0,0,0.25)',
      heatmapHigh: 'rgba(0,0,0,0.45)',

      neonCyan: '#111827',
      neonPurple: '#1f2937',
      neonPink: '#374151',
      neonGreen: '#16a34a',
      neonOrange: '#d97706',

      rarityCommon: '#666666',
      rarityUncommon: '#16a34a',
      rarityRare: '#2563eb',
      rarityEpic: '#7c3aed',
      rarityLegendary: '#d97706',
      rarityMythic: '#dc2626',
    }
  },

  // Emerald Night (dark green accent)
  'emerald-night': {
    name: 'emerald-night',
    displayName: 'Emerald Night',
    description: 'Deep dark with emerald accents',
    isDark: true,
    colors: {
      primary: '#10b981',
      secondary: '#34d399',
      tertiary: '#059669',

      background: '#0b0f0e',
      backgroundSecondary: '#101915',
      backgroundTertiary: '#0f1f19',
      surface: '#163229',
      surfaceHover: '#1b3d32',

      textPrimary: '#e6fffb',
      textSecondary: '#c7f5ea',
      textTertiary: '#9fe8d7',
      textInverse: '#000000',

      border: '#215c4c',
      borderHover: '#2a6e5b',
      borderActive: '#10b981',

      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#22d6d0',

      buy: '#10b981',
      sell: '#ef4444',
      neutral: '#9fe8d7',

      chartBackground: '#0b0f0e',
      chartGrid: '#215c4c',
      chartText: '#e6fffb',
      candleUp: '#10b981',
      candleDown: '#ef4444',
      wickUp: '#10b981',
      wickDown: '#ef4444',

      heatmapLow: 'rgba(16,185,129,0.25)',
      heatmapMedium: 'rgba(16,185,129,0.45)',
      heatmapHigh: 'rgba(16,185,129,0.65)',

      neonCyan: '#22d6d0',
      neonPurple: '#7c3aed',
      neonPink: '#ec4899',
      neonGreen: '#10b981',
      neonOrange: '#f59e0b',

      rarityCommon: '#9fe8d7',
      rarityUncommon: '#10b981',
      rarityRare: '#22d6d0',
      rarityEpic: '#7c3aed',
      rarityLegendary: '#f59e0b',
      rarityMythic: '#ec4899',
    }
  },

  // Royal Blue Light
  'royal-blue-light': {
    name: 'royal-blue-light',
    displayName: 'Royal Blue Light',
    description: 'Crisp light theme with royal blue accents',
    isDark: false,
    colors: {
      primary: '#3b82f6',
      secondary: '#6366f1',
      tertiary: '#06b6d4',

      background: '#f7f9ff',
      backgroundSecondary: '#ffffff',
      backgroundTertiary: '#eef2ff',
      surface: '#dde7ff',
      surfaceHover: '#cddaff',

      textPrimary: '#0b1020',
      textSecondary: '#1f2a44',
      textTertiary: '#475569',
      textInverse: '#ffffff',

      border: '#c7d2fe',
      borderHover: '#b4c3fd',
      borderActive: '#3b82f6',

      success: '#16a34a',
      warning: '#d97706',
      error: '#dc2626',
      info: '#2563eb',

      buy: '#16a34a',
      sell: '#dc2626',
      neutral: '#475569',

      chartBackground: '#ffffff',
      chartGrid: '#c7d2fe',
      chartText: '#0b1020',
      candleUp: '#16a34a',
      candleDown: '#dc2626',
      wickUp: '#16a34a',
      wickDown: '#dc2626',

      heatmapLow: 'rgba(59,130,246,0.25)',
      heatmapMedium: 'rgba(99,102,241,0.35)',
      heatmapHigh: 'rgba(6,182,212,0.45)',

      neonCyan: '#06b6d4',
      neonPurple: '#6366f1',
      neonPink: '#ec4899',
      neonGreen: '#16a34a',
      neonOrange: '#d97706',

      rarityCommon: '#b9a183',
      rarityUncommon: '#16a34a',
      rarityRare: '#3b82f6',
      rarityEpic: '#6366f1',
      rarityLegendary: '#d97706',
      rarityMythic: '#ec4899',
    }
  },

  // Dracula Dark
  'dracula-dark': {
    name: 'dracula-dark',
    displayName: 'Dracula Dark',
    description: 'Classic Dracula palette with soft contrasts',
    isDark: true,
    colors: {
      primary: '#bd93f9',
      secondary: '#ff79c6',
      tertiary: '#50fa7b',

      background: '#282a36',
      backgroundSecondary: '#1e1f29',
      backgroundTertiary: '#14151d',
      surface: '#303241',
      surfaceHover: '#3a3d4e',

      textPrimary: '#f8f8f2',
      textSecondary: '#e6e6e6',
      textTertiary: '#cfcfd3',
      textInverse: '#000000',

      border: '#3a3d4e',
      borderHover: '#4a4e63',
      borderActive: '#bd93f9',

      success: '#50fa7b',
      warning: '#f1fa8c',
      error: '#ff5555',
      info: '#8be9fd',

      buy: '#50fa7b',
      sell: '#ff5555',
      neutral: '#cfcfd3',

      chartBackground: '#282a36',
      chartGrid: '#3a3d4e',
      chartText: '#f8f8f2',
      candleUp: '#50fa7b',
      candleDown: '#ff5555',
      wickUp: '#50fa7b',
      wickDown: '#ff5555',

      heatmapLow: 'rgba(189,147,249,0.25)',
      heatmapMedium: 'rgba(80,250,123,0.35)',
      heatmapHigh: 'rgba(255,121,198,0.45)',

      neonCyan: '#8be9fd',
      neonPurple: '#bd93f9',
      neonPink: '#ff79c6',
      neonGreen: '#50fa7b',
      neonOrange: '#f1fa8c',

      rarityCommon: '#cfcfd3',
      rarityUncommon: '#50fa7b',
      rarityRare: '#8be9fd',
      rarityEpic: '#bd93f9',
      rarityLegendary: '#f1fa8c',
      rarityMythic: '#ff79c6',
    }
  },

  // Amber Night (warm dark)
  'amber-night': {
    name: 'amber-night',
    displayName: 'Amber Night',
    description: 'Warm dark theme with amber accents',
    isDark: true,
    colors: {
      primary: '#f59e0b',
      secondary: '#fb923c',
      tertiary: '#fbbf24',

      background: '#0f0d0a',
      backgroundSecondary: '#1a140c',
      backgroundTertiary: '#241a0d',
      surface: '#2c2010',
      surfaceHover: '#3a2914',

      textPrimary: '#fff7ed',
      textSecondary: '#fde68a',
      textTertiary: '#fbbf24',
      textInverse: '#000000',

      border: '#4a3a1a',
      borderHover: '#5c4a20',
      borderActive: '#f59e0b',

      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#fbbf24',

      buy: '#10b981',
      sell: '#ef4444',
      neutral: '#fde68a',

      chartBackground: '#0f0d0a',
      chartGrid: '#4a3a1a',
      chartText: '#fff7ed',
      candleUp: '#10b981',
      candleDown: '#ef4444',
      wickUp: '#10b981',
      wickDown: '#ef4444',

      heatmapLow: 'rgba(245,158,11,0.25)',
      heatmapMedium: 'rgba(251,191,36,0.35)',
      heatmapHigh: 'rgba(251,146,60,0.45)',

      neonCyan: '#22d6d0',
      neonPurple: '#a78bfa',
      neonPink: '#f472b6',
      neonGreen: '#10b981',
      neonOrange: '#f59e0b',

      rarityCommon: '#fde68a',
      rarityUncommon: '#10b981',
      rarityRare: '#f59e0b',
      rarityEpic: '#a78bfa',
      rarityLegendary: '#fbbf24',
      rarityMythic: '#f472b6',
    }
  }
};

export const defaultTheme = 'jester-deck';

export const getTheme = (themeName: string): Theme => {
  return themes[themeName] || themes[defaultTheme];
};

export const getAllThemes = (): Theme[] => {
  return Object.values(themes);
};
